/**
 * @fileoverview Main application component with error boundary and proper routing
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from './components/error/ErrorBoundary';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';

/**
 * Lazy loaded page components for better performance
 */
const Home = React.lazy(() => import('./pages/Home'));
const Mentoring = React.lazy(() => import('./pages/Mentoring'));
const Newsletter = React.lazy(() => import('./pages/Newsletter'));
const Blog = React.lazy(() => import('./pages/Blog'));
const About = React.lazy(() => import('./pages/About'));

/**
 * Loading component for lazy-loaded routes
 */
const LoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-pulse text-primary-DEFAULT">Loading...</div>
  </div>
);

/**
 * Error fallback component for the main application
 */
const AppErrorFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen bg-neutral-50 dark:bg-neutral-900">
    <div className="max-w-md w-full mx-auto p-8 text-center">
      <h1 className="text-2xl font-bold text-primary-dark dark:text-primary-light mb-4">
        Oops! Something went wrong
      </h1>
      <p className="text-neutral-600 dark:text-neutral-400 mb-8">
        We're having trouble loading the application. Please try refreshing the page.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-6 py-3 bg-primary text-white rounded-full hover:bg-primary-dark transition-colors"
      >
        Refresh Page
      </button>
    </div>
  </div>
);

/**
 * Not found page component
 */
const NotFound: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen bg-neutral-50 dark:bg-neutral-900">
    <div className="max-w-md w-full mx-auto p-8 text-center">
      <h1 className="text-4xl font-bold text-primary-dark dark:text-primary-light mb-4">
        404
      </h1>
      <p className="text-xl text-neutral-600 dark:text-neutral-400 mb-8">
        Page not found
      </p>
      <a
        href="/"
        className="px-6 py-3 bg-primary text-white rounded-full hover:bg-primary-dark transition-colors inline-block"
      >
        Go Home
      </a>
    </div>
  </div>
);

/**
 * Main application component
 * 
 * @returns {JSX.Element} The rendered application
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary fallback={<AppErrorFallback />}>
      <Router>
        <div className="min-h-screen bg-white dark:bg-neutral-900 transition-colors">
          {/* Global Header */}
          <Header />
          
          {/* Main Content */}
          <main className="container mx-auto px-4 lg:px-8">
            <React.Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/mentoring" element={<Mentoring />} />
                <Route path="/newsletter" element={<Newsletter />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/about" element={<About />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </React.Suspense>
          </main>

          {/* Global Footer */}
          <Footer />
        </div>
      </Router>
    </ErrorBoundary>
  );
};

export default App;